// Inspired from https://pikax.me/vue-composable/composable/misc/vmodel.html

import { computed, getCurrentInstance, ref, watch } from 'vue'

/**
 * Helper to use a prop as a modelValue
 * Allows avoiding defining each time a computed with getter and setter as well as the defineEmit
 * Set linked to false if the prop is not necessarily reactive in the parent component.
 * IMPORTANT: When using linked = true, the new value will be updated in the next tick.
 *
 * Linked = true behavior is natively supported in Vue 3.4+ https://vuejs.org/guide/components/v-model
 * @return {import('vue').WritableComputedRef}
 */
export function useVModel(props, propName, linked = true) {
  const instance = getCurrentInstance()
  if (!instance) {
    throw new Error('useVModel must be called from the setup or lifecycle hook methods.')
  }

  if (!linked) {
    const modelValue = ref(props[propName])

    watch(modelValue, value => {
      if (props[propName] !== value) {
        instance.emit(`update:${propName}`, value)
      }
    })

    watch(() => props[propName], value => {
      if (modelValue.value !== value) {
        modelValue.value = value
      }
    })

    return modelValue
  }

  return computed({
    get() {
      return props[propName]
    },
    set(value) {
      instance.emit(`update:${propName}`, value)
    }
  })
}
